import { prepUrlForEnvironment } from '@sureapp/wl-toggle-common/dist/utils/url';
import produce from 'immer';

import KnockoutCopy from '@@/auto/components/KnockoutCopy/KnockoutCopy';
import { AUTO_DISCOUNTS } from '@@/auto/constants/autoDiscounts';
import { featureFlag } from '@@/auto/constants/featureFlags';
import { TConfig, TConfigInput, TConfigParams } from '@@/auto/interfaces';
import CKLogo from '@@/common/assets/credit-karma/image/credit-karma.svg';
import InverseToggleLogo from '@@/common/assets/toggle/image/TAFC-inverse.svg';
import LightToggleLogo from '@@/common/assets/toggle/image/TAFC-ongreen_1.svg';

const mfaSuccessHost = prepUrlForEnvironment({
  url: process.env.REACT_APP_MFA_SUCCESS_REDIRECT_HOST,
});

function getConfig({
  isFeatureEnabled,
}: TConfigParams): DeepPartial<TConfigInput> {
  return {
    coverageStartDateConfig: (
      coverageStartDateConfig: TConfig['coverageStartDateConfig']
    ) => ({
      ...coverageStartDateConfig,
      infoCopy: null,
      labelCopy: 'Select a start date',
    }),
    discountsConfig: {
      partnerDiscounts: [
        {
          title: 'Credit Karma Member',
          description: AUTO_DISCOUNTS.affinity.description,
        },
      ],
    },
    featureFlags: (featureFlags: TConfig['featureFlags']) => ({
      ...featureFlags,
      isAccountManagementEnabled: isFeatureEnabled(
        featureFlag.IS_CK_ACCOUNT_MANAGEMENT_ENABLED
      ),
      isAutoPurchaseFlowEnabled: isFeatureEnabled(
        featureFlag.IS_CK_AUTO_PURCHASE_FLOW_ENABLED
      ),
      isAutoQuoteLinkExchangeEnabled: isFeatureEnabled(
        featureFlag.IS_CK_AUTO_QUOTE_LINK_EXCHANGE_ENABLED
      ),
      isDirectToAutoQuoteEnabled: isFeatureEnabled(
        featureFlag.IS_CK_DIRECT_TO_AUTO_QUOTE_ENABLED
      ),
      isLogoRedirectInAutoPurchaseFlowEnabled: isFeatureEnabled(
        featureFlag.IS_CK_LOGO_REDIRECT_IN_AUTO_PURCHASE_FLOW_ENABLED
      ),
      isPrivacyAndTermsEnabled: isFeatureEnabled(
        featureFlag.IS_CK_PRIVACY_AND_TERMS_ENABLED
      ),
      isBillingEnabled: isFeatureEnabled(featureFlag.IS_CK_BILLING_ENABLED),
      isQuoteAssistForTenantEnabled:
        isFeatureEnabled(featureFlag.IS_QUOTE_ASSIST_ENABLED) &&
        isFeatureEnabled(featureFlag.IS_CK_QUOTE_ASSIST_ENABLED),
      isRentersQuoteCreationEnabled: isFeatureEnabled(
        featureFlag.IS_CK_RENTERS_QUOTE_CREATION_ENABLED
      ),
      isSelfServiceEnabled: isFeatureEnabled(
        featureFlag.IS_CK_SELF_SERVICE_ENABLED
      ),
    }),
    footerConfig: (footerConfig: TConfig['footerConfig']) => ({
      ...footerConfig,
      hasNavItems: false,
      tenantPhoneVariant: 'second-row',
      getSocialLinks: () => [],
    }),
    iidKnockoutCopy: {
      featureFlag: 'isUpdatedCreditKarmaKnockoutCopy',
      subHeader: <KnockoutCopy phoneNumber="855-449-2814" />,
    },
    localforageDbName: 'credit_karma',
    logos: {
      default: CKLogo,
      light: CKLogo,
      title: 'Toggle + Credit Karma logo',
      toggleDefault: InverseToggleLogo,
      toggleLight: LightToggleLogo,
    },
    navBarConfig: {
      getLinks: () => null,
    },
    notifyConfig: {
      discountCopy:
        'Congratulations! Being a Credit Karma member unlocks a discount and exclusive features.',
    },
    partnerCode: 'toggle_credit_karma',
    primaryVehicleDataSource: undefined,
    purchaseFlowConfig: (
      purchaseFlowConfig: TConfig['purchaseFlowConfig']
    ) => ({
      ...purchaseFlowConfig,
      isCheckoutToggleCustomerDefault: undefined,
    }),
    quoteExpirationCondition: {
      maxElapsedTimeMinutes: 60,
      onConfirmDestination: 'https://www.creditkarma.com/',
      timestampKey: 'created_at',
    },
    redirectUrls: (redirectUrls: TConfig['redirectUrls']) => ({
      ...redirectUrls,
      indexRedirectUrl: 'https://www.creditkarma.com/',
      logoRedirectUrl: undefined,
      mfaSuccessRedirectUrl: `${mfaSuccessHost}my-account`,
      navBlockerRedirectUrl: 'https://www.creditkarma.com/',
    }),
    signUpViewConfig: (signUpViewConfig: TConfig['signUpViewConfig']) =>
      produce(signUpViewConfig, (draft) => {
        Object.assign(draft.copy, {
          header:
            "Get auto insurance and peace of mind in minutes. Customize it so you don't have to pay for coverage you don't want.",
          informationalHeader: 'But first...',
          informationalSubHeader: 'Tell us a little about yourself.',
        });
      }),
    tenantDisplayName: 'Credit Karma',
    tenantPhoneNumber: '1-855-449-2814',
    utmSourceTenantPhoneNumberMap: {},
    vehicleManagementCopyConfig: (
      vehicleManagementCopyConfig: TConfig['vehicleManagementCopyConfig']
    ) => ({
      ...vehicleManagementCopyConfig,
      subheader:
        "We've added the vehicles from your current policy. Feel free to add more or edit our suggestions.",
    }),
  };
}

export default getConfig;
